import { z } from "zod";

/**
 * THE VERSION FLOOR — the endpoint that makes "the app is a version you cannot recall"
 * survivable.
 *
 * The app asks at launch. Below the floor it shows a blocking screen with a link to the
 * store instead of failing in whatever way an outdated client fails in — usually a parse
 * error on a screen that has nothing to do with the change.
 *
 * Raising the floor is a deliberate act with a cost: everyone below it is stopped until
 * they update. It is for a change that genuinely cannot be made backwards compatible, not
 * for tidiness.
 */
export const minimumVersionSchema = z.object({
  /** Semver. Builds below this are blocked. */
  minimum: z.string(),
  /** Semver. Builds below this get a dismissible nudge. */
  recommended: z.string(),
  /** Where the update lives, per platform. */
  storeUrl: z.object({ ios: z.string(), android: z.string(), web: z.string() }),
});

export type MinimumVersion = z.infer<typeof minimumVersionSchema>;

/**
 * THE FOUR VERBS A CLIENT LOGS WITH, WRITTEN ONCE.
 *
 * The app's `log.*` and the service's logger already use these four names so that one habit
 * works on both sides of the wire. Now that a server value can raise the app's threshold,
 * the four names are on the wire too — and a wire vocabulary that exists twice is a
 * vocabulary that eventually differs by one entry.
 */
export const logLevelSchema = z.enum(["debug", "info", "warn", "error"]);

export type LogLevel = z.infer<typeof logLevelSchema>;

/**
 * THE CLIENT MANIFEST — what an installed build should do, answered by the service.
 *
 * A build-time value is compiled into a binary somebody installed, so the setting on their
 * phone is the one that shipped months ago: changing it costs an App Store review and then
 * every user choosing to update. Everything here is instead read from the service, which
 * means changing it costs a deploy — seconds rather than days.
 *
 * THREE PROPERTIES MATTER MORE THAN THE FIELDS, and all three live on the client:
 *
 *   1. Every value has a permissive default hard-coded in the app, so a service that is
 *      down, slow or answering nonsense leaves the app behaving exactly as it does with no
 *      manifest at all. A manifest that can strand every install is worse than no manifest.
 *   2. The last good answer is cached and reused, and only ignored once it is old enough to
 *      be untrustworthy — at which point the fallback is the defaults, never nothing.
 *   3. It is read at the point of use, so an answer that changes while the app is open
 *      changes what the app does without a relaunch.
 *
 * WHY THE VERSION FLOOR IS IN HERE. `/v1/meta/minimum-version` is a one-field manifest that
 * answers the same question this does — "what should this build do?" — and two things
 * answering it can disagree. One fetch, one cache, one staleness story. The old endpoint
 * stays, unchanged, because builds already on phones call it and cannot be recalled.
 *
 * WHAT IS DELIBERATELY NOT HERE: a fleet-wide pause, a throttle percentage, gating on a
 * third party's version. Those belong to a long-running desktop sync process, not to this
 * app, and a config surface grows far more easily than it shrinks.
 */
export const clientManifestSchema = z.object({
  /** The floor and the nudge, folded in — the same three fields the old endpoint answers. */
  versionFloor: minimumVersionSchema,
  /**
   * The lowest level the app still emits. `debug` — the default — emits everything, which
   * is what the app does today. Raising it is how a noisy build is quietened, or a specific
   * build turned all the way up, for the person having the problem now rather than for
   * whoever installs the next release.
   */
  logLevel: logLevelSchema,
  /**
   * SESSION REPLAY, WHICH IS TWO NUMBERS AND NOT ONE — because only one of them is a
   * setting.
   *
   * `onErrorSampleRate` is the billing lever: what fraction of sessions that hit an error
   * get their buffered window attached. Turning it down is a cost decision.
   *
   * `sessionSampleRate` is not a setting at all. Above zero it turns error diagnostics into
   * continuous recording of people using the app, which is the thing this app's privacy
   * position says it does not do. It is on the wire only so that the app can be seen to
   * refuse it: the client clamps it to zero whatever arrives here. Collapsing the two into
   * one number is what would make that refusal impossible to express.
   */
  replay: z.object({
    sessionSampleRate: z.number().min(0).max(1),
    onErrorSampleRate: z.number().min(0).max(1),
  }),
  /** Flat, name -> on/off. A shape with rules in it is a rules engine nobody asked for. */
  featureFlags: z.record(z.string(), z.boolean()),
  /**
   * The over-the-air update channel. Carried with NO consumer yet, deliberately: the kill
   * switch has to exist before the channel does, or the first thing shipped over the air is
   * a channel with no way to stop it.
   */
  ota: z.object({
    /** False means: do not look for a bundle at all. */
    enabled: z.boolean(),
    /** Semver. A bundle below this is refused. */
    minimumBundleVersion: z.string(),
  }),
  /** Empty means "not configured" — the app shows no support link rather than a dead one. */
  supportUrl: z.string(),
});

export type ClientManifest = z.infer<typeof clientManifestSchema>;

export const healthSchema = z.object({
  status: z.literal("ok"),
  env: z.enum(["dev", "staging", "production"]),
  version: z.string(),
  /** Migration count applied to the database this service is talking to. */
  schemaVersion: z.number(),
});

export type Health = z.infer<typeof healthSchema>;
